const configCache = new Map();

export function clearConfigCache() {
  configCache.clear();
}

export async function loadConfig(organizationId, acquisitionFormId) {
  const baseUrl = getCheckoutApiBaseUrl();

  if (!baseUrl) {
    console.error('[TrackmobAcquisitionForm] Checkout API base URL not configured or detected');
    return null;
  }

  if (!organizationId) {
    console.warn('[TrackmobAcquisitionForm] organizationId is required to load acquisitionForm config');
    return null;
  }

  if (!acquisitionFormId) {
    console.warn('[TrackmobAcquisitionForm] acquisitionFormId is required to load acquisitionForm config');
    return null;
  }

  const cacheKey = `${organizationId}|${acquisitionFormId}`;
  const cached = configCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  const pending = fetchConfig(baseUrl, organizationId, acquisitionFormId);
  configCache.set(cacheKey, pending);

  const config = await pending;

  if (!config) {
    configCache.delete(cacheKey);
  }

  return config;
}

async function fetchConfig(baseUrl, organizationId, acquisitionFormId) {
  try {
    const url = `${baseUrl.replace(/\/$/, '')}/v1/acquisitionforms/${acquisitionFormId}/config`;
    console.log(`[TrackmobAcquisitionForm] Fetching config from: ${url}`);

    const response = await fetch(url);

    if (response.status === 404) {
      console.warn(`[TrackmobAcquisitionForm] AcquisitionForm not found for acquisitionFormId: "${acquisitionFormId}"`);
      return null;
    }

    if (!response.ok) {
      console.error(`[TrackmobAcquisitionForm] Failed to fetch acquisitionForm config. Status: ${response.status}`);
      return null;
    }

    const data = await response.json();

    if (!data.config) {
      console.warn('[TrackmobAcquisitionForm] AcquisitionForm response missing config');
      return null;
    }

    return {
      ...data.config,
      OrganizationId: organizationId,
      AcquisitionFormId: data.acquisitionFormId,
      CaptchaSiteKey: data.captchaSiteKey || null,
      CaptchaRequired: Boolean(data.captchaRequired),
      TokenExPublicKey: data.tokenExPublicKey || null
    };
  } catch (error) {
    console.error('[TrackmobAcquisitionForm] Error loading acquisitionForm config:', error);
    return null;
  }
}

export function getCheckoutApiBaseUrl() {
  const scriptUrl = getAcquisitionFormScriptUrl();
  if (scriptUrl) {
    return new URL(scriptUrl).origin;
  }

  const configuredBaseUrl = import.meta.env.VITE_CHECKOUT_API_URL;
  if (configuredBaseUrl) {
    return configuredBaseUrl;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return null;
}

export function getAcquisitionFormScriptUrl() {
  if (typeof document === 'undefined') {
    return null;
  }

  if (document.currentScript?.src) {
    return document.currentScript.src;
  }

  const acquisitionFormScript = document.querySelector('script[data-tm-script="acquisition-form"][src]');
  if (acquisitionFormScript?.src) {
    return acquisitionFormScript.src;
  }

  const scriptByPath = Array.from(document.querySelectorAll('script[src]'))
    .find((script) => /\/widget\/widget(-projects)?\.js/.test(script.src));

  return scriptByPath?.src ?? null;
}
